/**
 * The per-path fallbacks — `Copy For TMS` and `Download Word`
 * (`09-tms-write-paths.md` §4).
 *
 * > *"Every push screen carries an alternative that keeps the system out of TMS entirely.
 * > **Using TMS at all is the author's choice on every occasion.**"* … *"Fallback controls
 * > must ship alongside the push controls **from day one**, not be added if the push fails.
 * > They are the honest default, and they are what makes the system never dependent on TMS."*
 *
 * **Neither control touches the API.** Both render from the payload already on screen, so
 * they work when the transport is unavailable, when TMS is unreachable, and when the network
 * is gone — which is the whole of their purpose. Nothing here can fail because of TMS.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WORD FORMAT, AND WHY IT IS THIS ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §4 asks for a *"Word download"* and does not name a format. This build emits a
 * **Word-compatible HTML document** — `.doc`, `application/msword`, with Word's own
 * `xmlns:w` office header — rather than a true `.docx`.
 *
 *  * A `.docx` is a ZIP of several XML parts. Producing one in the browser means a zip
 *    library and a package writer: a dependency, a bundle-size cost and a new failure mode,
 *    on a control whose entire reason for existing is that it works when other things do not.
 *  * The `.doc` HTML form opens in Word, Pages and LibreOffice, keeps the table and the
 *    headings, and prints. That is what the TD needs it for — handing a report to somebody,
 *    or pasting from it into TMS by hand.
 *  * It is generated by a **pure function**, {@link buildWordDocument}, so its content is
 *    asserted in a test rather than trusted.
 *
 * If a later slice needs a true `.docx` — a template with the FIH letterhead, say — this
 * function is the single place that changes.
 */

import { useState } from "react";

import { formatPayloadForTms, marksTotalsRow, tidyCriterionDisplayName, type TmsPushPayload } from "@hockeytd/shared";

import { tournamentFileStamp } from "../../app/tournamentTime.js";

/** A file about to be handed to the browser: name, type and content, all decided in one place. */
export interface DownloadableDocument {
  filename: string;
  mimeType: string;
  content: string;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * `AG2026-Marks-DE-VRIES-Joost-2026-10-04-0912.doc` — path, subject, and the instant.
 *
 * **RUN 83 — the stamp is the tournament's clock**, not the device's. The file this names is a
 * fallback record of a push: it is saved, filed and attached, and it is the only date the
 * document carries once it has left the app. Two TDs downloading the same marking a minute
 * apart used to get names claiming different days, and neither necessarily the day the venue
 * was on.
 */
export function documentFilename(payload: TmsPushPayload, at: Date): string {
  const stamp = tournamentFileStamp(at);
  const who = payload.subjectName.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return `AG2026-${payload.kind === "marks" ? "Marks" : "Report"}-${who}-${stamp}.doc`;
}

/**
 * The criteria table, closing with the shared totals row.
 *
 * Every criterion of the set is a row, in the set's own order, `Video Umpire` included — the
 * document is a record of the marking, and a row missing from it reads as a criterion that
 * does not exist.
 */
function marksTable(payload: Extract<TmsPushPayload, { kind: "marks" }>): string {
  const totals = marksTotalsRow(payload);
  const rows = payload.marks
    .map(
      (mark) =>
        `<tr><td>${escapeHtml(tidyCriterionDisplayName(mark.name))}</td><td>${String(mark.weight)}</td>` +
        `<td>${mark.na ? "N/A" : String(mark.value ?? "—")}</td></tr>`,
    )
    .join("");

  return (
    `<table border="1" cellspacing="0" cellpadding="6">` +
    `<tr><th>Criterion</th><th>Weight</th><th>Mark</th></tr>` +
    rows +
    `<tr><td><b>${escapeHtml(totals.label)}</b></td>` +
    `<td>${escapeHtml(totals.weightText)}</td>` +
    `<td><b>${escapeHtml(totals.markText)}</b></td></tr>` +
    `</table>`
  );
}

/**
 * The Word-compatible document for one payload.
 *
 * Marks render as a table — criterion, weight, mark — closing with the Performance Outcome,
 * in the set's own order and with every row present, `Video Umpire` included. A report
 * renders as its text, with the line breaks preserved, and nothing added: the `TD - ` /
 * `UM - ` prefixes are already in the assembled text and are not repeated here.
 *
 * The closing row's wording comes from `marksTotalsRow` in `@hockeytd/shared`, the same
 * function the screen and the clipboard use. This document is the copy a TD prints and hands
 * to somebody who was never near the screen, so it is the rendering that can least afford an
 * unlabelled number.
 */
export function buildWordDocument(payload: TmsPushPayload, at: Date = new Date()): DownloadableDocument {
  const title =
    payload.kind === "marks"
      ? `Marking — ${payload.subjectName}`
      : `Performance Report — ${payload.subjectName}`;

  const body =
    payload.kind === "marks"
      ? marksTable(payload)
      : `<p>${escapeHtml(payload.text).replace(/\n/g, "<br/>")}</p>`;

  return {
    filename: documentFilename(payload, at),
    mimeType: "application/msword",
    // The `xmlns:w` header is what makes Word open an HTML file as a document rather than
    // as a web page. British English throughout, per the standing copy rule.
    content:
      `<html xmlns:o="urn:schemas-microsoft-com:office:office" ` +
      `xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">` +
      `<head><meta charset="utf-8"><title>${escapeHtml(title)}</title></head><body>` +
      `<h1>${escapeHtml(title)}</h1>` +
      `<p>Whole-tournament record, prepared in the TD Control Centre. ` +
      `Draft — publishing remains a deliberate step inside TMS.</p>` +
      body +
      `</body></html>`,
  };
}

/** Hand a document to the browser. Separated from the building so the content is testable. */
function offerDownload(document_: DownloadableDocument): void {
  const blob = new Blob([document_.content], { type: `${document_.mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = document_.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * `Copy For TMS`'s clipboard write, with §B6's own fallback: *"`navigator.clipboard.writeText`
 * with a `document.execCommand("copy")` fallback"*, because a clipboard write is refused
 * outright in some browsers when the page is not focused, and the TD must not be left with
 * a control that silently does nothing.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const copied = document.execCommand("copy");
      area.remove();
      window.getSelection()?.removeAllRanges();
      return copied;
    } catch {
      return false;
    }
  }
}

export interface PushFallbacksProps {
  payload: TmsPushPayload;
  /** Injected in tests only. */
  copy?: (text: string) => Promise<boolean>;
  download?: (document_: DownloadableDocument) => void;
}

export function PushFallbacks({
  payload,
  copy = copyToClipboard,
  download = offerDownload,
}: PushFallbacksProps): React.JSX.Element {
  const [note, setNote] = useState<string | null>(null);

  const text = formatPayloadForTms(payload);

  return (
    <div className="pushfallbacks" data-testid="push-fallbacks">
      <button
        type="button"
        className="gbtn"
        data-testid="copy-for-tms"
        onClick={() => {
          void copy(text).then((ok) => {
            setNote(
              ok
                ? `Copied · ${String(text.length)} characters on the clipboard — paste them into TMS.`
                : "Copy blocked · select the text and copy it manually.",
            );
          });
        }}
      >
        Copy For TMS
      </button>

      <button
        type="button"
        className="gbtn"
        data-testid="download-word"
        onClick={() => {
          const file = buildWordDocument(payload);
          download(file);
          setNote(`Downloaded · ${file.filename}`);
        }}
      >
        Download Word
      </button>

      <span className="btnhint">
        Both work whether or not TMS can be reached — they are how this record leaves the
        Control Centre without it.
      </span>

      {note === null ? null : (
        <span className="pushfallbacks__note" role="status" data-testid="fallback-note">
          {note}
        </span>
      )}
    </div>
  );
}
